import { Lightning } from '@lightningjs/sdk'

import {
  cancel,
  getColor,
  grey50,
  grey600,
  grey960,
  primary500,
  primary600,
  transparent,
} from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { TvBadgeStyle } from '../../types/flo-enums'
import { TvBadgeModel } from '../../types/flo-experience'
import { convertSecsToTime } from '../../utils/datetime'
import { getIcon } from '../../utils/image'

const ICON_SIZE = 28
export default class TvBadge extends Lightning.Component {
  private _data: TvBadgeModel
  private _padding = 10

  static _template() {
    return {
      w: 0,
      h: 36,
      BoxShadow: {},
      BadgeContent: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: transparent,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: 4,
        },
        Icon: {
          h: ICON_SIZE,
          w: ICON_SIZE,
          x: (w) => w / 2,
          y: (h) => h / 2,
          mount: 0.5,
        },
        Label: {
          h: 32,
          x: (w) => w / 2,
          y: (h) => h / 2 + 1,
          mount: 0.5,
          text: {
            text: '',
            textColor: getColor(grey50),
            fontFace: UniNeueBold,
            fontSize: 24,
            textOverflow: 'ellipsis',
            maxLines: 1,
            maxLinesSuffix: '...',
          },
        },
      },
    }
  }

  set maxWidth(v: number) {
    this.label.patch({ text: { wordWrapWidth: v } })
  }

  get label() {
    return this.tag('Label')
  }

  get icon() {
    return this.tag('Icon')
  }

  get badgeContent() {
    return this.tag('BadgeContent')
  }

  get boxShadow() {
    return this.tag('BoxShadow')
  }

  get padding() {
    return this._padding
  }

  set data(v: TvBadgeModel) {
    this._data = v
    this.updateBadgeLayout(v)
  }

  set duration(v: number) {
    this.renderDurationBadge(v)
  }

  set showSpinner(show: boolean) {
    if (show && this._data.icon) {
      this.icon.patch({
        texture: null,
        rect: true,
        shader: {
          type: Lightning.shaders.Spinner2,
          color: getColor(primary500),
          backgroundColor: transparent,
          stroke: 8,
          showDot: true,
        },
      })
    } else {
      this.updateBadgeLayout(this._data)
    }
  }

  _init() {
    this.label.on('txLoaded', () => {
      this.w = this.label.renderWidth + this.padding * 2
    })

    this.icon.on('txLoaded', () => {
      this.w = this.h
    })
  }

  updateBadgeLayout(badge: TvBadgeModel) {
    const badgeConfig = this.getBadgeColor(badge?.style)

    this.badgeContent.patch({
      color: badgeConfig.bgColor,
      ...(badge?.icon && {
        Icon: {
          color: badgeConfig.color,
          texture: Lightning.Tools.getSvgTexture(getIcon(badge?.icon?.style), ICON_SIZE, ICON_SIZE),
          shader: null,
        },
      }),
      ...(!badge?.icon && {
        Label: {
          text: {
            text: badge?.text?.text?.toUpperCase() ?? '',
            textColor: badgeConfig.color,
          },
        },
      }),
    })

    if (badge?.icon && (badge?.style === 'light-shadow' || badge?.style === 'dark-shadow')) {
      this.adjustBoxShadow()
    }
  }

  renderDurationBadge(secs: number) {
    const duration = convertSecsToTime(secs)

    this.badgeContent.patch({
      color: getColor(grey600),
      Label: {
        text: {
          text: duration,
          textColor: getColor(grey50),
        },
      },
    })
  }

  getBadgeColor(style: TvBadgeStyle) {
    switch (style) {
      case 'dark-shadow':
        return { bgColor: getColor(grey960), color: getColor(grey50) }
      case 'light-shadow':
        return { bgColor: getColor(grey50), color: getColor(grey960) }
      case 'live':
        return { bgColor: getColor(primary600), color: getColor(grey50) }
      case 'standard':
        return { bgColor: getColor(grey600), color: getColor(grey50) }
      case 'cancel':
        return { bgColor: getColor(cancel), color: getColor(grey960) }
      case 'alternate':
        return { bgColor: getColor(grey50), color: getColor(grey960) }
      default:
        return { bgColor: getColor(grey600), color: getColor(grey50) }
    }
  }

  adjustBoxShadow() {
    const offset = 5
    const shadowSize = this.h + offset
    const rectRadius = 4
    const blur = offset + 5
    const margin = blur

    this.boxShadow.patch({
      x: (offset / 2 + margin) * -1,
      y: (offset / 2 + margin) * -1,
      color: getColor(grey50, 0.15),
      texture: Lightning.Tools.getShadowRect(shadowSize, shadowSize, rectRadius, blur, margin),
    })
  }
}
